import {
  Drawer, message, Select,
} from 'antd';
import PropTypes from 'prop-types';
import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { filterSelectDropdown } from '../../helpers/helpers';
import ContactAddView from '../../contacts/ContactAddView';
import DrawerSubmitFooter from '../containers/DrawerSubmitFooter';
import Permissions from '../../auth/Permissions';
import { createCustomer } from '../../contacts/customers/state/customers.actions';
import { createVendor } from '../../contacts/vendors/state/vendors.actions';
import OnTraccrButton from '../buttons/OnTraccrButton';
import { formatCustomFieldCreatePayload } from '../../helpers/costcodeHelpers';

/**
 * NOTE: A component with similar functionality exists in `ContactSelector.jsx`
 *
 * Key Differences:
 * - `ContactSelector` stores values in a form while `CustomerVendorSelector` allows setting an
 *   `onChange` handler
 * - `ContactSelector` only allows a single value for the contact while `CustomerVendorSelector`
 *   allows multiple
 * - `ContactSelector` does not handle the actual dispatching of the contact creation while
 *   `CustomerVendorSelector` will automatically dispatch the creation of the contact
 *
 * Overall, `ContactSelector` is better tailored as a form input while `CustomerVendorSelector`
 * is closer to a stand-alone input component
 */
export default function CustomerVendorSelector({
  options,
  value,
  onChange,
  disabled,
  numAnswers,
  openLimit,
  style,
  isDisplay,
  overrideDivisionIds = null,
  isVendor = false,
}) {
  const contactType = isVendor ? 'Vendor' : 'Customer';
  const hasWritePerms = Permissions.has(isVendor ? 'VENDORS_WRITE' : 'CUSTOMERS_WRITE');

  const dispatch = useDispatch();

  const formRef = useRef();

  const [showContactAddDrawer, setShowContactAddDrawer] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [customFields, setCustomFields] = useState([]);

  const selectMode = useMemo(() => (numAnswers > 1 || openLimit ? 'multiple' : null), [numAnswers, openLimit]);

  const selectOptions = useMemo(() => (
    options.map(({
      id: optionId,
      name: optionName,
      label: optionLabel,
    }) => {
      const label = optionLabel ?? optionName;

      return (
        <Select.Option
          value={optionId}
          key={optionId}
          label={label}
          name={optionName}
          disabled={
            !openLimit
            && numAnswers > 1
            && value.length >= numAnswers
            && !value.includes(optionId)
          }
        >
          {label}
        </Select.Option>
      );
    })), [options, value]);

  const onAdd = useCallback(() => {
    if (!hasWritePerms) return;
    setShowContactAddDrawer(true);
  }, [hasWritePerms]);

  const onClose = useCallback(async () => {
    setShowContactAddDrawer(false);
    try {
      await formRef?.current?.resetFields();
    } catch (error) {
      message.error('Something went wrong. Please try again.');
    }

    setCurrentStep(0);
  }, []);

  const handleFormDataValidation = useCallback(async () => {
    const form = formRef.current;
    const values = await form.getFieldsValue();
    const payload = formatCustomFieldCreatePayload({
      payload: values,
      customFields,
    });

    if (payload?.error || !payload) {
      setErrors(payload?.errorMap);
      throw new Error('Validation error with custom fields');
    }

    return form.validateFields();
  }, [formRef.current, customFields]);

  const handleOnSubmit = useCallback(async () => {
    const form = formRef.current;
    try {
      const isValidated = await handleFormDataValidation();
      if (!isValidated) return;
      const values = await form.getFieldsValue();
      let payload = formatCustomFieldCreatePayload({
        payload: values,
        customFields,
      });

      // Filter payload based on vendor or customer schema requirements
      if (isVendor) {
        // Vendor schema: contactSchema + type
        const {
          billingRateId,
          customBillingRate,
          materialDiscount,
          downstreamBucketIds,
          files,
          customData,
          ...vendorPayload
        } = payload;
        payload = vendorPayload;
      }
      // Customer payload already includes all necessary fields

      setErrors({});
      const data = await dispatch(isVendor ? createVendor(payload) : createCustomer(payload));

      const contactKey = isVendor ? 'vendor' : 'customer';
      const {
        [contactKey]: {
          id: contactId,
          name: contactName,
        } = {},
      } = data;

      if (contactId) {
        message.success(`${contactType} created successfully.`);

        // onSelectChange is not able to recognize the new customer/vendor option in the state
        // so we need to manually pass it along to the onChange function
        // NOTE: AntD's Select already passes a second argument along with every onChange for
        // the selected value(s), so we can just pass the new option as the second argument
        const newContactOption = { key: contactId, label: contactName, value: contactId };
        if (selectMode !== 'multiple') onChange(contactId, newContactOption);

        onClose();
      } else {
        message.warning(`${contactType} could not be created. Please check your responses and try again.`);
      }
    } catch (error) {
      message.error('Something went wrong. Please check your responses and try again.');
    } finally {
      setLoading(false);
    }
  }, [formRef.current, selectMode, onChange, customFields, isVendor]);

  const handleOnNext = useCallback(async () => {
    setLoading(true);

    // Handle General Info Validation
    let isValidated = false;
    try {
      isValidated = await handleFormDataValidation();
    } catch (error) {
      setLoading(false);
    }
    if (!isValidated) return;

    if (currentStep === 0) {
      setCurrentStep(currentStep + 1);
      setLoading(false);
    } else if (currentStep === 1) {
      await handleOnSubmit();
    }
  }, [currentStep, customFields]);

  const handleOnBack = useCallback(() => {
    if (currentStep === 0) return;
    setCurrentStep(currentStep - 1);
  }, [currentStep]);

  const dropdownRender = useCallback((menu) => (
    <>
      {menu}
      <div
        style={{
          padding: '8px 8px 4px 8px',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'end',
          borderTop: '1px solid #f0f0f0',
        }}
      >

        <OnTraccrButton
          onClick={onAdd}
          title={`Add ${contactType}`}
        />
      </div>

    </>
  ), [onAdd, isVendor]);

  return (
    <>
      <Select
        style={{ ...style, flexGrow: 1 }}
        mode={selectMode}
        onChange={onChange}
        value={value}
        showSearch={!isDisplay}
        allowClear
        disabled={!isDisplay && disabled}
        filterOption={filterSelectDropdown}
        placeholder={`Select a ${contactType.toLowerCase()}`}
        dropdownRender={(!isDisplay && hasWritePerms) ? dropdownRender : undefined}
        open={isDisplay ? false : undefined}
      >
        {selectOptions}
      </Select>
      <Drawer
        title={`Add ${contactType}`}
        width={900}
        visible={showContactAddDrawer}
        onClose={onClose}
        destroyOnClose
      >
        <ContactAddView
          isVendor={isVendor}
          form={{ formRef, currentStep, isAdd: true }}
          overrideDivisionIds={overrideDivisionIds}
          customFields={customFields}
          setCustomFields={setCustomFields}
          errors={errors}
        />
        <DrawerSubmitFooter
          onAction={onClose}
          onClose={currentStep !== 0 && handleOnBack}
          onSubmit={handleOnNext}
          actionTitle="Cancel"
          closeTitle="Back"
          submitTitle={currentStep < 1 ? 'Next' : 'Submit'}
          loading={loading}
        />
      </Drawer>
    </>
  );
}

CustomerVendorSelector.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    number: PropTypes.string,
    label: PropTypes.string,
    subNames: PropTypes.arrayOf(PropTypes.string),
  })).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
  ]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  numAnswers: PropTypes.number,
  openLimit: PropTypes.bool,
  style: PropTypes.shape({}),
  isDisplay: PropTypes.bool,
  overrideDivisionIds: PropTypes.arrayOf(PropTypes.string),
  isVendor: PropTypes.bool,
};

CustomerVendorSelector.defaultProps = {
  value: null,
  disabled: false,
  numAnswers: 1,
  openLimit: false,
  style: {},
  isDisplay: false,
  overrideDivisionIds: null,
  isVendor: false,
};

